import { Hero, SectionHeader } from '@/components/marketing/primitives.jsx';

export default function Privacy() {
  return (
    <>
      <Hero
        eyebrow="Privacy"
        title="The privacy model is the product."
        description="NACIS is not a data lake. Records stay with the authorities that own them. We broker verifiable exchange — we do not aggregate or resell."
      />

      <section className="py-16 md:py-20">
        <div className="container-narrow">
          <div className="prose-marketing">
            <p className="text-sm text-slate-500">Last updated: April 2026</p>

            <SectionHeader
              eyebrow="Principles"
              title="Seven commitments we make to every person NACIS touches."
            />

            <h2>1. Minimisation</h2>
            <p>
              We collect only the fields required to perform a specific subsystem function. Free-text indicators
              in OSINT signals are hashed at ingest so raw content is never stored. Device fingerprints are hashed
              server-side; the raw values never persist.
            </p>

            <h2>2. Purpose limitation</h2>
            <p>
              Data collected for one subsystem is not re-used in another without an explicit, audited cross-reference.
              IFPS data does not flow into VERIS. RERR media is not used to train OSINT models.
            </p>

            <h2>3. Retention</h2>
            <p>
              Each subsystem declares a retention policy aligned to its authorising statute. After retention expires,
              records are sealed from normal query paths and eventually purged. Audit-log retention is longer by design
              to preserve integrity verification.
            </p>

            <h2>4. Access transparency</h2>
            <p>
              If you are the subject of a NACIS record, you can request an access-transparency report showing who
              accessed your data, when, and under what authorisation — subject to authorised investigative holds.
            </p>

            <h2>5. Data subject rights</h2>
            <ul>
              <li>
                <strong>Access:</strong> request a copy of records about you held in NACIS.
              </li>
              <li>
                <strong>Correction:</strong> submit corrections through the owning authority (NACIS routes these
                to the right agency or institution).
              </li>
              <li>
                <strong>Dispute:</strong> contest an identity state, a dedup match, or an enforcement action through
                the appropriate subsystem's dispute workflow.
              </li>
              <li>
                <strong>Erasure:</strong> scoped to subsystems where erasure does not conflict with legal retention
                obligations.
              </li>
            </ul>

            <h2>6. No surveillance use</h2>
            <p>
              NACIS does not harvest private communications. OSINT ingestion is restricted to allow-listed public
              sources. NACIS does not perform real-time facial recognition against civilian populations.
            </p>

            <h2>7. Oversight</h2>
            <p>
              An independent Inspector General role has standing read access across NACIS. IG findings are public-by-default
              unless explicitly sealed by judicial order. The IG publishes an annual transparency report.
            </p>

            <h2>How we process children's data</h2>
            <p>
              NACIS is not directed at children under 13. Citizen accounts require age affirmation. Reports about
              children are routed to appropriate child-protection authorities under the governing subsystem's
              statute.
            </p>

            <h2>International data</h2>
            <p>
              NACIS operates under United States federal jurisdiction. Non-US data subjects contacted through the
              platform receive notice of applicable legal frameworks.
            </p>

            <h2>How to reach us</h2>
            <p>
              For privacy inquiries, data-subject requests, or questions about a specific record, use the Contact
              page and select the Privacy inquiry type. For authorised investigative holds or judicial process,
              contact through the Judicial integration layer.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
